import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getAsset } from "../helpers/helper";
import dummyUser from "../assets/images/dummy/dummy-user.png";
import { useGetAllShopsByUserIdQuery } from "../features/shop/shopApiSlice";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../config/axios";

/** Extract a single string image path from mixed shapes (string | object | array) */
const extractFirstImage = (field) => {
  if (!field) return null;
  const normalize = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    if (typeof item === "object")
      return (
        item.url ||
        item.filename ||
        item.path ||
        item.image ||
        item.imageUrl ||
        item.src ||
        null
      );
    return String(item);
  };
  if (Array.isArray(field)) {
    for (const f of field) {
      const v = normalize(f);
      if (v) return v;
    }
    return null;
  }
  return normalize(field);
};

/** Resolve image value safely and fallback to dummyUser */
export const resolveProfileImage = (val) => {
  if (!val) return dummyUser;
  let v = val;
  if (Array.isArray(v)) v = v[0];
  if (!v) return dummyUser;
  if (typeof v === "object") {
    v =
      v.url ||
      v.filename ||
      v.path ||
      v.image ||
      v.imageUrl ||
      v.src ||
      v.name ||
      null;
    if (Array.isArray(v)) v = v[0];
  }
  if (!v || typeof v !== "string") return dummyUser;
  const s = v.trim();
  if (!s) return dummyUser;
  if (/^https?:\/\//.test(s)) return s;
  try {
    return getAsset(s);
  } catch {
    return s || dummyUser;
  }
};

// ─── hook

const useRightSectionState = (onProfileSelect, initialProfile) => {
  const { name } = useSelector((state) => state.user);

  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);

  // RTK Query – skip until we have a valid userId
  const {
    data: shopData,
    isLoading,
    isFetching,
    isError,
    refetch: refetchProfiles,
  } = useGetAllShopsByUserIdQuery(name?._id, {
    skip: !name?._id,
  });

  // Build profile list whenever RTK data arrives
  useEffect(() => {
    if (!name?._id || isLoading || !shopData) return;

    const localShopId = localStorage.getItem("shopid");
    const messengerProfileKey = `messenger-profile:${name._id}`;
    const savedMessengerProfile = localStorage.getItem(messengerProfileKey);

    const shopProfiles = shopData?.data || [];
    const isMarketer = shopData?.marketingAgent === true;

    const generalPublicProfile = {
      ...name,
      isGeneralPublic: true,
      isMarketer: false,
      name: name.fullname || name.name,
      businessType: "generalPublic",
      imageUrl: extractFirstImage(
        name.imageUrl ||
          name.profilePic ||
          name.image ||
          name.profileImage ||
          name.avatar,
      ),
    };

    const marketerProfile = {
      ...name,
      isGeneralPublic: false,
      isMarketer: true,
      name: name.fullname || name.name,
      businessType: "marketerShop",
      imageUrl: extractFirstImage(
        name.imageUrl ||
          name.profilePic ||
          name.image ||
          name.profileImage ||
          name.avatar,
      ),
    };

    const normalizedShopProfiles = shopProfiles.map((p) => {
      const id =
        p.id ||
        p._id ||
        p.shopId ||
        p.shopid ||
        p.companyid ||
        p.serviceid ||
        "";
      const images = [
        extractFirstImage(p.imageUrl),
        extractFirstImage(p.profilePic),
        extractFirstImage(p.profileImage),
        extractFirstImage(p.image),
        extractFirstImage(p.images),
        extractFirstImage(p.logo),
        extractFirstImage(p.avatar),
      ].filter(Boolean);

      const displayName =
        p.name ||
        p.shopname ||
        p.shopName ||
        p.profileName ||
        p.servicename ||
        p.companyname ||
        p.businessName ||
        `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
        "Unknown Shop";

      return {
        ...p,
        id:
          id ||
          p.id ||
          p._id ||
          p.shopId ||
          p.shopid ||
          p.companyid ||
          p.serviceid,
        shopid:
          p.shopid ||
          p.shopId ||
          p.companyid ||
          p.serviceid ||
          p.id ||
          p._id ||
          "",
        serviceid: p.serviceid || "",
        companyid: p.companyid || "",
        images,
        imageUrl: images[0] || null,
        displayName,
        name: displayName,
        businessType: p.businessType || p.type || p.category || "shop",
      };
    });

    let finalProfiles = isMarketer
      ? [generalPublicProfile, marketerProfile, ...normalizedShopProfiles]
      : [generalPublicProfile, ...normalizedShopProfiles];

    // Determine initial selected profile
    let selected = null;
    if (initialProfile?.type === "user") {
      selected = generalPublicProfile;
    } else if (initialProfile?.type === "marketer") {
      selected = marketerProfile || generalPublicProfile;
    } else if (initialProfile?.type === "shop") {
      const requestedShopId = String(initialProfile.profileId || "");
      selected =
        normalizedShopProfiles.find((profile) =>
          [
            profile.id,
            profile._id,
            profile.shopid,
            profile.shopId,
            profile.serviceid,
            profile.companyid,
          ]
            .filter(Boolean)
            .some((profileId) => String(profileId) === requestedShopId),
        ) || null;

      // Keep directly requested shops selectable even when the all-shops
      // response omits that profile (for example, a shop type not included by
      // the generic profile endpoint). The connection list can still load by ID.
      if (!selected && requestedShopId) {
        selected = {
          ...initialProfile,
          id: requestedShopId,
          _id: requestedShopId,
          shopid: requestedShopId,
          businessType: initialProfile.businessType || "shop",
          displayName:
            initialProfile.displayName || initialProfile.name || "Shop",
          name: initialProfile.displayName || initialProfile.name || "Shop",
        };
        finalProfiles = [...finalProfiles, selected];
      }
    }

    setProfiles(finalProfiles);

    if (!selected && !initialProfile && savedMessengerProfile === "user") {
      selected = generalPublicProfile;
    } else if (
      !selected &&
      !initialProfile &&
      savedMessengerProfile === "marketer"
    ) {
      selected = generalPublicProfile;
      localStorage.setItem(messengerProfileKey, "user");
      localStorage.removeItem("shopid");
    } else if (
      !selected &&
      !initialProfile &&
      savedMessengerProfile?.startsWith("shop:")
    ) {
      const savedShopId = savedMessengerProfile.slice("shop:".length);
      selected =
        normalizedShopProfiles.find((profile) =>
          [
            profile.id,
            profile._id,
            profile.shopid,
            profile.shopId,
            profile.serviceid,
            profile.companyid,
          ]
            .filter(Boolean)
            .some((id) => String(id) === savedShopId),
        ) || null;
    }

    if (!selected && !initialProfile && !savedMessengerProfile && localShopId) {
      selected =
        normalizedShopProfiles.find((profile) =>
          [
            profile.id,
            profile._id,
            profile.shopid,
            profile.shopId,
            profile.serviceid,
            profile.companyid,
          ]
            .filter(Boolean)
            .some((id) => String(id) === String(localShopId)),
        ) || null;
    }
    if (!selected) {
      selected = generalPublicProfile;
      localStorage.removeItem("shopid");
    }

    if (initialProfile?.type === "user") {
      localStorage.setItem(messengerProfileKey, "user");
    } else if (initialProfile?.type === "marketer" && selected.isMarketer) {
      localStorage.setItem(messengerProfileKey, "marketer");
    } else if (initialProfile?.type === "shop" && !selected.isGeneralPublic) {
      localStorage.setItem(
        messengerProfileKey,
        `shop:${selected.id || selected._id || initialProfile.profileId}`,
      );
      localStorage.setItem(
        "shopid",
        selected.shopid || selected.shopId || initialProfile.profileId,
      );
    }

    setSelectedProfile(selected);
    onProfileSelect?.(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shopData,
    isLoading,
    name?._id,
    initialProfile?.type,
    initialProfile?.profileId,
  ]);

  // Connection counting effect removed — connectionCount is not rendered anywhere and was firing redundant searchConnectedShops requests for every profile on mount.

  // Real-time unread counts and dropdown auto-open
  useEffect(() => {
    if (!name?._id || !profiles.length) return;

    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL);
    }
    const socket = socketRef.current;

    const handleConnect = () => {
      profiles.forEach((p) => {
        const idToJoin = p.isGeneralPublic ? name._id : p.id;
        if (idToJoin) socket.emit("join_user", idToJoin);
      });
    };

    if (socket.connected) {
      handleConnect();
    } else {
      socket.on("connect", handleConnect);
    }

    const handleReceiveMessage = (message) => {
      const receiverId = message.receiverId;
      const senderId = message.sender || message.sendershopid;

      if (!receiverId) return;

      // Don't increment unread count if we are the sender
      if (String(senderId) === String(receiverId)) return;

      setProfiles((prev) =>
        prev.map((p) => {
          const profileIds = [
            p.isGeneralPublic ? name._id : p.id,
            p.shopid,
            p.shopId,
            p._id,
          ]
            .filter(Boolean)
            .map(String);
          if (profileIds.includes(String(receiverId))) {
            return { ...p, unreadCount: (Number(p.unreadCount) || 0) + 1 };
          }
          return p;
        }),
      );
    };

    const handleMessagesRead = (event) => {
      const readBy = event?.readBy || event?.userId || event?.receiverId;
      if (!readBy) return;

      setProfiles((prev) =>
        prev.map((profile) => {
          const profileIds = [
            profile.isGeneralPublic ? name._id : profile.id,
            profile.shopid,
            profile.shopId,
            profile._id,
          ]
            .filter(Boolean)
            .map(String);
          return profileIds.includes(String(readBy))
            ? { ...profile, unreadCount: 0 }
            : profile;
        }),
      );
    };

    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("messages_read", handleMessagesRead);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("messages_read", handleMessagesRead);
    };
  }, [name?._id, profiles.length]); // Re-run when profiles are loaded

  // Handle profile selection from dropdown
  const handleSelect = (profile) => {
    if (!profile) return;

    const selectedProfileIds = [
      selectedProfile?.shopid,
      selectedProfile?.shopId,
      selectedProfile?.serviceid,
      selectedProfile?.companyid,
      selectedProfile?.id,
      selectedProfile?._id,
    ]
      .filter(Boolean)
      .map(String);
    const nextProfileIds = [
      profile.shopid,
      profile.shopId,
      profile.serviceid,
      profile.companyid,
      profile.id,
      profile._id,
    ]
      .filter(Boolean)
      .map(String);
    const isSameProfile =
      (selectedProfile?.isGeneralPublic && profile.isGeneralPublic) ||
      (selectedProfile?.isMarketer && profile.isMarketer) ||
      selectedProfileIds.some((id) => nextProfileIds.includes(id));

    if (isSameProfile) {
      setDropdownOpen(false);
      return;
    }

    setSelectedProfile(profile);
    setDropdownOpen(false);

    if (profile.isGeneralPublic) {
      localStorage.setItem(`messenger-profile:${name._id}`, "user");
      localStorage.removeItem("shopid");
    } else if (profile.isMarketer) {
      localStorage.setItem(`messenger-profile:${name._id}`, "marketer");
    } else {
      const profileId =
        profile.id ||
        profile._id ||
        profile.shopid ||
        profile.shopId ||
        profile.serviceid ||
        profile.companyid;
      const shopId =
        profile.shopid ||
        profile.shopId ||
        profile.id ||
        profile.serviceid ||
        profile.companyid;
      if (profileId) {
        localStorage.setItem(
          `messenger-profile:${name._id}`,
          `shop:${profileId}`,
        );
      }
      if (shopId) {
        localStorage.setItem("shopid", shopId);
      }
    }

    onProfileSelect?.(profile, { userInitiated: true });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return {
    profiles,
    selectedProfile,
    dropdownOpen,
    setDropdownOpen,
    dropdownRef,
    handleSelect,
    isLoading: (isLoading || isFetching) && !shopData,
    isError,
    refetchProfiles,
  };
};

export default useRightSectionState;
